/**
 * glør Duft-Auswahl — Proxy für den nativen Varianten-Picker.
 *
 * Update-sicher: hängt sich NICHT in Theme-Interna. Es klickt lediglich den
 * passenden nativen <variant-picker>-Radio (per Options-Wert) und löst ein
 * change-Event aus — die native Engine erledigt Variantenwechsel, Preis,
 * Medien und Add-to-Cart. Der native Picker wird optisch ausgeblendet, seine
 * Radios bleiben funktionsfähig.
 */
class GlrScentSelect extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId || '';
    this.optionName = (this.dataset.optionName || '').trim().toLowerCase();
    this.productUrl = (this.dataset.productUrl || '').split('?')[0];
    this.opts = Array.from(this.querySelectorAll('[data-glr-scent-opt]'));

    this.native = this.#findNativePicker();
    this.#hideNative();

    for (const btn of this.opts) {
      btn.addEventListener('click', () => this.#select(btn));
    }

    // Falls die Variante anderswo geändert wird (oder nach einem Section-Morph),
    // aktive Karte nachziehen.
    document.addEventListener('change', this.#reflect);
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.#reflect);
  }

  /** @returns {Element | null} */
  #findNativePicker() {
    const pickers = Array.from(document.querySelectorAll('variant-picker'));
    if (pickers.length === 0) return null;
    const byId = pickers.find((p) => p instanceof HTMLElement && p.dataset.productId === this.productId);
    return byId || pickers[0];
  }

  #hideNative() {
    if (this.native instanceof HTMLElement) this.native.style.display = 'none';
  }

  /** @param {string} value */
  #radioFor(value) {
    if (!this.native) return null;
    const radios = Array.from(this.native.querySelectorAll('input[type="radio"]'));
    const want = value.trim().toLowerCase();
    return (
      radios.find((r) => r instanceof HTMLInputElement && r.value.trim().toLowerCase() === want) || null
    );
  }

  /** @param {HTMLElement} btn */
  #select(btn) {
    const value = btn.dataset.optionValue || '';
    const variantId = btn.dataset.variantId || '';

    this.#setActive(value);

    // 1) Nativen Picker umschalten (Radio ODER Dropdown) — löst die native Kette
    //    (Preis/Medien/Add-to-Cart) aus. Gibt true zurück, wenn vorhanden.
    const drove = this.#driveNative(value);

    // 2) Varianten-ID direkt ins/in die Warenkorb-Formular(e) schreiben (Kauf-Garantie).
    if (variantId) this.#setFormVariant(variantId);

    // 3) Gibt es KEINEN nativen Picker, feuern wir das ProductSelectEvent selbst —
    //    dann aktualisieren Preis (product-price.js), Medien & Buttons trotzdem.
    if (!drove && variantId) this.#dispatchProductSelect(variantId);

    // native könnte durch einen Section-Morph neu gerendert werden → wieder verstecken
    this.native = this.#findNativePicker();
    this.#hideNative();
  }


  /**
   * Adresse fuer das Nachladen einer anderen Variante. `view` und die
   * Vorschau-Parameter muessen mit, sonst antwortet Shopify mit der
   * STANDARD-Vorlage bzw. aus dem veroeffentlichten Theme - und im frischen
   * HTML fehlen genau die Bloecke, die aktualisiert werden sollen.
   * @param {number|string} variantId
   * @returns {string}
   */
  #renderUrl(variantId) {
    const base = new URL(this.productUrl, window.location.origin);
    const here = new URL(window.location.href);
    if (here.pathname === base.pathname) {
      for (const key of ['view', 'preview_theme_id', '_fd', 'pb', '_ab', '_bt']) {
        const value = here.searchParams.get(key);
        if (value !== null) base.searchParams.set(key, value);
      }
    }
    base.searchParams.set('variant', String(variantId));
    return base.toString();
  }

  /**
   * Ohne nativen Picker: Varianten-HTML laden und ProductSelectEvent feuern, damit
   * das Theme (Preis/Medien/Buttons) auf die gewählte Variante aktualisiert.
   * @param {string} variantId
   */
  async #dispatchProductSelect(variantId) {
    if (!this.productUrl) return;
    let ProductSelectEvent;
    try {
      ({ ProductSelectEvent } = await import('@shopify/events'));
    } catch (e) {
      return; // events nicht verfügbar
    }
    const deferred = ProductSelectEvent.createPromise();
    // auf dem Element feuern → bubbelt zur Section, wo product-price.js lauscht.
    this.dispatchEvent(
      new ProductSelectEvent({
        product: { id: this.productId },
        selectedOptions: [],
        detail: {},
        promise: deferred.promise,
      })
    );
    try {
      const res = await fetch(this.#renderUrl(variantId));
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      deferred.resolve({
        variant: null,
        detail: { html, productId: this.productId, resource: { id: variantId } },
      });
    } catch (err) {
      deferred.reject?.(err);
      console.warn('[glr-scent-select] Preis-Update fehlgeschlagen:', err);
    }
  }

  /**
   * Schaltet den nativen Picker auf den Options-Wert (Radio oder Select).
   * @param {string} value
   * @returns {boolean} true, wenn ein Steuerelement gefunden & umgeschaltet wurde
   */
  #driveNative(value) {
    if (!this.native) return false;
    const want = value.trim().toLowerCase();

    const radio = this.#radioFor(value);
    if (radio) {
      // Echter Klick löst die volle native Kette aus (variant-picker.js →
      // ProductSelectEvent → Preis/Medien/Add-to-Cart aktualisieren).
      if (!radio.checked) radio.click();
      return true;
    }

    const select = this.native.querySelector('select');
    if (select instanceof HTMLSelectElement) {
      const opt = Array.from(select.options).find(
        (o) => o.value.trim().toLowerCase() === want || o.textContent.trim().toLowerCase() === want
      );
      if (opt) {
        select.value = opt.value;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    return false;
  }

  /**
   * Setzt die Varianten-ID in allen Warenkorb-Formularen dieses Produkts.
   * @param {string} variantId
   */
  #setFormVariant(variantId) {
    const inputs = new Set();
    document.querySelectorAll('product-form-component').forEach((pf) => {
      if (!(pf instanceof HTMLElement)) return;
      if (pf.dataset.productId && pf.dataset.productId !== this.productId) return;
      pf.querySelectorAll('[name="id"]').forEach((i) => inputs.add(i));
    });
    if (inputs.size === 0) {
      document.querySelectorAll('form[action*="/cart/add"] [name="id"]').forEach((i) => inputs.add(i));
    }
    inputs.forEach((input) => {
      if (input instanceof HTMLInputElement && input.value !== variantId) {
        input.value = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /** @param {string} value */
  #setActive(value) {
    const want = value.trim().toLowerCase();
    for (const o of this.opts) {
      const active = (o.dataset.optionValue || '').trim().toLowerCase() === want;
      o.classList.toggle('is-active', active);
      o.setAttribute('aria-checked', active ? 'true' : 'false');
    }
  }

  /** @param {Event} event */
  #reflect = (event) => {
    const t = event.target;
    if (!(t instanceof HTMLInputElement) || t.type !== 'radio' || !t.checked) return;
    if (!this.native || !this.native.contains(t)) return;
    this.#setActive(t.value);
  };
}

if (!customElements.get('glr-scent-select')) {
  customElements.define('glr-scent-select', GlrScentSelect);
}
